/**
 * Attente d'un contenu (refonte éditoriale).
 *
 * ### Le constat
 *
 * Huit écrans écrivaient chacun leur propre attente, et toujours la même
 * ligne : `<p className="text-sm text-foreground-muted">Chargement…</p>`.
 * Trois défauts, dont deux invisibles :
 *
 * - **rien ne bouge.** Un mot gris immobile ne se distingue pas d'un écran
 *   qui a fini de charger et n'a rien trouvé. Sur une connexion lente, on
 *   attend devant ce qu'on prend pour un résultat ;
 * - **rien n'est annoncé.** Aucun de ces huit endroits ne portait de
 *   `role="status"` : à la synthèse vocale, l'écran restait muet pendant
 *   l'attente puis changeait sans prévenir ;
 * - **la hauteur s'effondre.** La ligne fait 20 px, le contenu qui la
 *   remplace en fait plusieurs centaines : la page saute au moment de
 *   l'échange, et ce qu'on s'apprêtait à toucher se déplace.
 *
 * La bibliothèque, elle, avait déjà la bonne réponse depuis US-23 — un
 * squelette calqué sur les cartes réelles. Elle était seule à l'avoir.
 *
 * ### Ce que ce composant fait, et ne fait pas
 *
 * Il ne prétend pas remplacer un squelette : dessiner la forme exacte de ce
 * qui arrive reste supérieur, et la bibliothèque garde le sien. Il donne aux
 * sept autres écrans ce qu'ils n'avaient pas — un signe qui bouge, une
 * annonce, et une hauteur réservée — sans demander à chacun de deviner la
 * forme de son contenu.
 *
 * Les trois barres pulsent **ensemble** et non en cascade : une cascade se
 * lit comme une progression, or rien ici ne progresse — on ne sait pas
 * combien de temps la requête prendra. Promettre un avancement qu'on ne
 * mesure pas est le même défaut qu'une barre de progression truquée.
 */
export interface LoadingProps {
  /**
   * Ce qu'on attend, en toutes lettres, pour l'annonce vocale : « les
   * recettes », « votre journal ». `aria-live` lit le libellé complet, et
   * « Chargement… » seul ne dit pas de quoi.
   */
  label: string;
  /** Nombre de lignes réservées. Trois par défaut — assez pour que la page ne saute pas, pas assez pour mentir sur la quantité à venir. */
  lines?: number;
  className?: string;
}

export function Loading({ label, lines = 3, className = '' }: LoadingProps): React.JSX.Element {
  return (
    <div
      data-testid="loading"
      // `status` et non `alert` : une attente n'interrompt pas ce qu'on est
      // en train de lire. `aria-busy` porte l'état à l'assistance technique,
      // comme sur les boutons (`ui/states.ts`).
      role="status"
      aria-busy="true"
      className={`flex flex-col gap-3 ${className}`.trim()}
    >
      <span className="sr-only">Chargement — {label}</span>
      {Array.from({ length: lines }, (_, index) => (
        <span
          key={index}
          aria-hidden="true"
          // Largeurs décroissantes : trois barres de largeur égale se lisent
          // comme un tableau, trois barres inégales comme du texte à venir.
          className="h-4 animate-pulse rounded-control bg-surface-sunken"
          style={{ width: `${String(100 - index * 18)}%`, maxWidth: '32rem' }}
        />
      ))}
    </div>
  );
}
